import { PPStation } from '../core/models/ppstation';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import * as moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Station, PlaylistResult } from '../core';
import { ElasticSearchHit, ElasticSearchResult } from './transcription-search.service';

export interface SearchKeywordGrid {
  station: String;
  transcription: String;
  audioPositionSeconds: Number;
  durationMillis: number;
  startDate: Date;
  rawTranscription: String;
  hit: ElasticSearchHit;
  stationDetails: Station;
  playlistDetails: PlaylistResult;
  show?: boolean;
  detailRow?: boolean;
  index?: number;
  icon?: string;
}

export interface ErrorGrid {
  hasError: boolean;
  message: any;
}


export class SearchHitsDataSource implements DataSource<SearchKeywordGrid> {
  /**
   * Pattern to split single/compound keywords in a list
   * Example:
   *    Keyword:"\"Great Toyota\" Nissan \"Honda\""
   *    Returns: [Great Toyota, Nissan, Honda]
   *
   * @private
   * @memberof SearchHitsDataSource
   */
  private keywordSplitRegex = /\w+|"[^"]+"/g;

  private rows: BehaviorSubject<SearchKeywordGrid[]>;

  private loadingSubject: BehaviorSubject<boolean>;
  private errorSubject: BehaviorSubject<boolean>;
  public hasDataReturned = true;
  public loading$;
  public error$;

  public total = 0;

  public rowsObject: any;

  constructor() {
    this.rows = new BehaviorSubject<SearchKeywordGrid[]>([]);
    this.loadingSubject = new BehaviorSubject<boolean>(false);
    this.errorSubject = new BehaviorSubject<boolean>(false);

    this.loading$ = this.loadingSubject.asObservable();
    this.error$ = this.errorSubject.asObservable();
  }

  connect(): Observable<SearchKeywordGrid[]> {
    return this.rows.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.rows.complete();
    this.loadingSubject.complete();
    this.errorSubject.complete();
  }

  extractDurationAndKeywordAtFromTranscript(transcript: string, keywordString: string) {

    const keywords = keywordString.match(this.keywordSplitRegex);

    if (keywords) {
      for (const keyword of keywords) {
        const nonQuotedKeyword = `<>:${keyword.replace(/"/g, '').replace(/ /g, ';{(\\d+):(\\d+)}<>:')};`;
        const kIndex = transcript.search(new RegExp(nonQuotedKeyword, 'gi'));

        if (!Number.isNaN(kIndex) && kIndex >= 0) {
          const subWord = transcript.substring(kIndex - 30, kIndex);
          const keywordAt = subWord.substring(subWord.lastIndexOf('{') + 1, subWord.lastIndexOf(':'));
          const duration = subWord.substring(subWord.lastIndexOf(':') + 1, subWord.lastIndexOf('}'));

          return { 'keywordAt': parseInt(keywordAt, 10), 'duration': parseInt(duration, 10) };
        }
      }
    }

    return { 'keywordAt': 0, 'duration': 0 };
  }
  sanitizeTranscript(transcript: string) {
    const reg = />:(.*?);/g;
    let match, sanitizedStr = '';
    while (match = reg.exec(transcript)) {
      sanitizedStr += match[1] + ' ';
    }
    return sanitizedStr;
  }

  getHighlightedPhrase(shortTranscript: string, keyword: string): string {
    const keywords = keyword.match(this.keywordSplitRegex);
    let result = shortTranscript;

    if (keywords) {
      for (const singleKeyword of keywords) {
        const nonQuotedKeyword = singleKeyword.replace(/"/g, '');
        result = result.replace(new RegExp(`(^|\\W)(${nonQuotedKeyword})(?!\\w)`, 'gi'),
          `$1<span style="color: #4c84fe">$2</span>`);
      }
    }

    return `<span>${result}</span>`;
  }

   getShortWords(transcript: string, keyword: string) {
    const keywords = keyword.match(this.keywordSplitRegex);

     if (keywords) {
      for (const singleKeyword of keywords) {
        const regExp = new RegExp(`(?:[a-zA-Z0-9\'-.]+[^a-zA-Z0-9\'-.]+){0,5}\\b`
          + singleKeyword.replace(/"/g, '')
          + `\\b(?:[^a-zA-Z\'-]+[a-zA-Z\'-]+){0,10}`, 'ig'
        );
        const matches = regExp.exec(transcript);

        if (matches && (matches.length > 0)) {
          return matches[0];
        } 
      }
    } else {
      const regExp = new RegExp(`(?:[a-zA-Z0-9\'-.]+[^a-zA-Z0-9\'-.]+){0,15}`, 'ig');
      const matches = regExp.exec(transcript);

      if (matches && (matches.length > 0)) {
        return matches[0];
      }
    } 

    return transcript;
  }

  loadSearchHits(searchKey: string,
    searchHits: Observable<ElasticSearchResult>,
    concatData: boolean = false): Observable<ElasticSearchResult> {
    this.loadingSubject.next(true);
    const observable = searchHits.pipe(
      finalize(() => { this.loadingSubject.next(false); })
    );
    observable.subscribe(
      (result: ElasticSearchResult) => {
        const rows: SearchKeywordGrid[] = [];
        this.total = result.total;
        const hits: ElasticSearchHit[] = result.transcripts || [];
        hits.forEach((hit: ElasticSearchHit, index) => {
          const stationName: string = hit.stationName;
          const startTime: Date = moment.utc(hit.segmentStartTime, 'YYYY-MM-DDTHH:mm:ssa').toDate();
          let transcription: string = hit.transcript || '';
          let sanitizedTranscript: string = hit.transcript || '';
          let keywordDurationMap = { 'keywordAt': 0, 'duration': 0 };
          if (hit.transcript) {
            keywordDurationMap = this.extractDurationAndKeywordAtFromTranscript(hit.transcript, searchKey);
            sanitizedTranscript = this.sanitizeTranscript(hit.transcript);
           const shortWords = this.getShortWords(sanitizedTranscript, searchKey);
           transcription = this.getHighlightedPhrase(shortWords, searchKey);
          }

          const rowObject = {
            station: stationName,
            transcription: transcription,
            audioPositionSeconds: keywordDurationMap.keywordAt / 1000,
            durationMillis: Math.ceil(keywordDurationMap.duration / 1000),
            startDate: startTime,
            rawTranscription: this.getHighlightedPhrase(sanitizedTranscript, searchKey),
            hit: hit,
            stationDetails: null,
            playlistDetails: null,
            show: false,
            index: index,
            icon: 'chevron_right'
          };
          rows.push(rowObject, { detailRow: true, ...rowObject });
        });
        this.rows.next(rows);
        this.rowsObject = rows;
      },
      (error: any) => {
        this.rows.next([]);
        this.loadingSubject.next(false);
        this.errorSubject.next(error.status !== 400);
      }
    );
    return observable;
  }

  totalRecords(): number {
    return this.total;
  }

  clear() {
    this.rows.next([]);
    this.total = 0;
    this.errorSubject.next(false);
  }

}
